import React, { useState, useRef, useEffect } from 'react';

interface SwipeToDepartButtonProps {
    onSwipeComplete: () => void;
    text?: string;
    disabled?: boolean;
}

const SwipeToDepartButton: React.FC<SwipeToDepartButtonProps> = ({ onSwipeComplete, text = '向右滑动发车', disabled = false }) => {
    const [translateX, setTranslateX] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [initialTranslateX, setInitialTranslateX] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);

    const getContainerWidth = () => {
        return containerRef.current?.offsetWidth || 0;
    };

    const getButtonWidth = () => {
        return buttonRef.current?.offsetWidth || 0;
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        if (disabled) return;
        setIsDragging(true);
        setStartX(e.touches[0].clientX);
        setInitialTranslateX(translateX);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!isDragging || disabled) return;

        const currentX = e.touches[0].clientX;
        const diffX = currentX - startX;
        const newTranslateX = initialTranslateX + diffX;
        const containerWidth = getContainerWidth();
        const buttonWidth = getButtonWidth();
        const maxTranslateX = containerWidth - buttonWidth;

        // 限制滑动范围：只能向右滑动，不能超过容器宽度
        const clampedTranslateX = Math.max(0, Math.min(newTranslateX, maxTranslateX));
        setTranslateX(clampedTranslateX);
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
        const containerWidth = getContainerWidth();
        const buttonWidth = getButtonWidth();
        const maxTranslateX = containerWidth - buttonWidth;
        const threshold = maxTranslateX * 0.8; // 滑动到80%时触发

        if (translateX >= threshold) {
            // 滑动完成，触发回调
            onSwipeComplete();
            // 重置位置
            setTimeout(() => setTranslateX(0), 300);
        } else {
            // 未达到阈值，回弹到初始位置
            setTranslateX(0);
        }
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        if (disabled) return;
        setIsDragging(true);
        setStartX(e.clientX);
        setInitialTranslateX(translateX);
        e.preventDefault();
    };

    useEffect(() => {
        if (isDragging) {
            const handleGlobalMouseMove = (e: MouseEvent) => {
                const currentX = e.clientX;
                const diffX = currentX - startX;
                const newTranslateX = initialTranslateX + diffX;
                const containerWidth = getContainerWidth();
                const buttonWidth = getButtonWidth();
                const maxTranslateX = containerWidth - buttonWidth;

                const clampedTranslateX = Math.max(0, Math.min(newTranslateX, maxTranslateX));
                setTranslateX(clampedTranslateX);
            };

            const handleGlobalMouseUp = () => {
                handleTouchEnd();
            };

            document.addEventListener('mousemove', handleGlobalMouseMove);
            document.addEventListener('mouseup', handleGlobalMouseUp);

            return () => {
                document.removeEventListener('mousemove', handleGlobalMouseMove);
                document.removeEventListener('mouseup', handleGlobalMouseUp);
            };
        }
    }, [isDragging, startX, initialTranslateX, translateX]);

    const containerWidth = getContainerWidth();
    const buttonWidth = getButtonWidth();
    const maxTranslateX = containerWidth > 0 && buttonWidth > 0 ? containerWidth - buttonWidth : 0;
    const progress = maxTranslateX > 0 ? translateX / maxTranslateX : 0;
    // 已滑动覆盖宽度（到按钮右侧边缘），并使用圆角避免看起来是直角
    const filledWidth = Math.max(0, Math.min(containerWidth, translateX + buttonWidth));
    // 与容器 rounded-lg 一致的圆角（Tailwind rounded-lg ≈ 0.5rem = 8px）
    const cornerRadiusPx = 8;

    return (
        <div className="w-full flex flex-col gap-2">
            <div
                ref={containerRef}
                className="relative w-full h-12 rounded-lg overflow-hidden"
                style={{
                    backgroundColor: '#e5e7eb',
                    touchAction: 'none',
                }}
            >
                {/* 已滑动部分渐变背景：保持与容器相同的半圆角（rounded-lg 效果） */}
                <div
                    className="absolute left-0 top-0 h-full"
                    style={{
                        width: `${filledWidth}px`,
                        background: 'linear-gradient(to right, #b388ff, var(--adm-color-primary))',
                        transition: 'width 0.1s ease-out',
                        zIndex: 0,
                        borderTopLeftRadius: `${cornerRadiusPx}px`,
                        borderBottomLeftRadius: `${cornerRadiusPx}px`,
                        // 始终保持右侧为圆角，避免视觉上的直角
                        borderTopRightRadius: `${cornerRadiusPx}px`,
                        borderBottomRightRadius: `${cornerRadiusPx}px`,
                    }}
                />
                {/* 中间提示文字，置于灰色轨道中央 */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none text-sm text-gray-600" style={{ zIndex: 1 }}>
                    {text}
                </div>

                {/* 按钮 */}
                <div
                    ref={buttonRef}
                    className={`absolute left-0 top-0 h-full flex items-center justify-center px-4 transition-transform duration-200 rounded-lg shadow-md ${
                        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-grab active:cursor-grabbing'
                    }`}
                    style={{
                        backgroundColor: 'var(--adm-color-primary)',
                        transform: `translateX(${translateX}px)`,
                        touchAction: 'none',
                        userSelect: 'none',
                        minWidth: '90px',
                        zIndex: 2,
                    }}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    onMouseDown={handleMouseDown}
                >
                    <div className="flex items-center justify-center text-white">
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SwipeToDepartButton;

